import { useState } from "react";
import { Bot, Loader2, Phone } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import useWhatsApps from "../../hooks/useWhatsApps";
import { useChamada } from "../../context/Chamada/ChamadaContext";
import { conexaoFaz } from "../../helpers/capacidadesDoCanal";
import { identidadeDoJid } from "../../helpers/identidadeDoJid";
import toastError from "../../errors/toastError";
import useModoDeGravacao, {
	GRAVACAO_PERGUNTA,
} from "../../hooks/useModoDeGravacao";
import AvisoDeGravacao from "../AvisoDeGravacao";
import LigarComIa from "./LigarComIa";

// Ligar para um número, de qualquer tela.
//
// A discagem já existia, mas só na tela de Chamadas e só digitando o número —
// e quem quer ligar quase sempre está olhando para a pessoa: na ficha do
// contato, na conversa aberta. Este botão é o mesmo gesto nos três lugares,
// com uma regra só.
//
// Ele SOME quando não há conexão de voz, em vez de aparecer e devolver erro:
// prometer o que o canal não faz é a armadilha que `conexaoFaz` existe para
// evitar. Some também em grupo (ligação é de uma pessoa para outra) e em
// contato sem telefone — só-LID não tem para onde ligar.

const NUMERO_MINIMO = 10;

const BotaoDeVoz = ({
	numero,
	nome,
	isGroup = false,
	size = "icon",
	variant = "ghost",
	className = "",
	mostrarRotulo = false,
}) => {
	const [discando, setDiscando] = useState(false);
	// Conexão escolhida esperando a resposta do aviso de gravação.
	const [perguntando, setPerguntando] = useState(null);

	const { whatsApps } = useWhatsApps();
	const { discar, discarComIA, ativa, agentesDeVoz } = useChamada();
	const modoDeGravacao = useModoDeGravacao();

	// Só telefone disca. Tirar os não-dígitos e conferir o TAMANHO deixava um
	// LID passar (ele tem 15 dígitos), e o botão prometia uma ligação que o
	// sidecar não tem como completar.
	const { numero: telefone } = identidadeDoJid(numero);
	const conexoes = (whatsApps || []).filter((w) => conexaoFaz(w, "calls"));

	if (!conexoes.length || isGroup || telefone.length < NUMERO_MINIMO) {
		return null;
	}

	// Com a empresa em "perguntar", a escolha vem antes da ligação: depois de
	// discar seria tarde — o começo da conversa já teria passado.
	const pedirLigacao = (whatsappId) => {
		if (modoDeGravacao === GRAVACAO_PERGUNTA) {
			setPerguntando(whatsappId);
			return;
		}

		ligar(whatsappId, false);
	};

	const ligar = async (whatsappId, gravar) => {
		setPerguntando(null);
		setDiscando(true);

		try {
			await discar(Number(whatsappId), telefone, { gravar });
		} catch (err) {
			explicarFalha(err);
		}

		setDiscando(false);
	};

	// Ligar com agente de voz (Fase 5 do VAPI): a IA fala, não a pessoa. O
	// microfone não abre aqui — quem conversa é a ponte no servidor.
	const ligarComIa = async (whatsappId, voiceAgentId, objetivo) => {
		setDiscando(true);

		try {
			await discarComIA(Number(whatsappId), telefone, voiceAgentId, objetivo);
		} catch (err) {
			explicarFalha(err);
			throw err;
		} finally {
			setDiscando(false);
		}
	};

	function explicarFalha(err) {
		const codigo = err?.response?.data?.message || err?.response?.data?.error;

		// Limite do plano é informação, não falha: dizer o que fazer vale
		// mais do que repetir o código do erro. Mesmo texto da tela de
		// Chamadas, de propósito.
		if (codigo === "ERR_CONTACT_SUPPRESSED") {
			// Recusado ANTES de discar: nada tocou no telefone do cliente.
			toast.info(
				'Este contato está marcado como "não contatar". Veja o motivo na ficha do contato.'
			);
		} else if (codigo === "ERR_PLAN_LIMIT_SIMULTANEOUS_CALLS") {
			toast.info(
				"Limite de chamadas simultâneas do plano atingido. Espere uma chamada terminar ou fale com o administrador."
			);
		} else if (codigo === "ERR_PLAN_LIMIT_VOICE_AI_MINUTES") {
			toast.info(
				"Os minutos de IA de voz do plano acabaram neste mês. A IA volta no dia 1, ou fale com o administrador para ampliar o plano."
			);
		} else if (codigo?.startsWith("ERR_CALLER_HTTP_429")) {
			// O sidecar recusa por segurança contra ban, e o motivo dele vem
			// colado depois de ":" na mensagem — por isso startsWith, não
			// igualdade. Mesmo texto da tela de Chamadas, de propósito.
			toast.info(
				"Este número já está no teto de chamadas simultâneas do WhatsApp (proteção contra bloqueio). Espere uma chamada terminar ou disque por outra conexão de voz."
			);
		} else if (codigo?.startsWith("ERR_CALLER_HTTP_501")) {
			// O sidecar recusa ANTES de discar quando não tem onde gravar:
			// nada aconteceu ainda, e a chamada sai normalmente sem gravar.
			// Sem esta tradução a pessoa via o código cru, não ligava, e não
			// descobria que bastava desmarcar a gravação.
			toast.info(
				"Este servidor de voz não está configurado para gravar. Ligue sem gravação, ou peça ao administrador para configurar a pasta das gravações."
			);
		} else if (codigo === "ERR_VAPI_SEM_CREDENCIAL") {
			// Nada foi discado: sem chave da VAPI não há agente para falar.
			toast.info(
				"Falta a chave da VAPI para o agente de voz falar. Peça ao administrador para configurá-la em Configurações › IA."
			);
		} else if (codigo === "ERR_CALLER_FORA_DO_AR") {
			// O servidor de voz nem respondeu: não é a sessão, é o serviço.
			toast.error(
				"O servidor de voz não respondeu — a chamada não saiu. Tente de novo em alguns minutos ou avise o administrador."
			);
		} else if (codigo?.startsWith("ERR_CALLER_HTTP_409")) {
			// Sessão de voz fora do ar (sidecar reiniciado, conexão caída).
			// Mesmo texto da tela de Chamadas, de propósito.
			toast.error(
				"A conexão de voz não está conectada — a chamada não saiu. Aguarde a reconexão automática (até 2 min) ou religue na tela de Conexões."
			);
		} else {
			toastError(err);
		}
	}

	const dica = ativa
		? "Já há uma chamada em andamento"
		: `Ligar para ${nome || telefone}`;

	const conteudo = discando ? (
		<Loader2 className="h-4 w-4 animate-spin" />
	) : (
		<Phone className="h-4 w-4" />
	);

	// Uma conexão só: o clique liga. É o caso de quase toda empresa, e um menu
	// com uma opção só seria um passo a mais para nada.
	// `title` em vez do Tooltip do Radix: este botão entra em tela que não tem
	// TooltipProvider por perto, e o provider do Radix é obrigatório —
	// faltando, o componente derruba a tela em runtime.
	// O botão da IA SOME sem agente ativo (T4/T6 do handoff): botão desligado
	// não ensina nada, e quem não criou agente não tem o que escolher.
	const botaoIa = agentesDeVoz?.length ? (
		<LigarComIa
			agentes={agentesDeVoz}
			conexoes={conexoes}
			nome={nome || telefone}
			desabilitado={discando || Boolean(ativa)}
			size={size}
			variant={variant}
			className={className}
			mostrarRotulo={mostrarRotulo}
			aoLigar={ligarComIa}
			icone={<Bot className="h-4 w-4" />}
		/>
	) : null;

	const aviso = (
		<AvisoDeGravacao
			aberto={perguntando !== null}
			paraQuem={nome}
			aoFechar={() => setPerguntando(null)}
			aoEscolher={(gravar) => ligar(perguntando, gravar)}
		/>
	);

	if (conexoes.length === 1) {
		return (
			<>
			{aviso}
			<Button
				type="button"
				size={size}
				variant={variant}
				className={className}
				disabled={discando || Boolean(ativa)}
				aria-label={dica}
				title={dica}
				onClick={(e) => {
					// Na linha de uma lista, o clique no botão não pode abrir o
					// item.
					e.stopPropagation();
					pedirLigacao(conexoes[0].id);
				}}
			>
				{conteudo}
				{mostrarRotulo ? "Ligar" : null}
			</Button>
			{botaoIa}
			</>
		);
	}

	return (
		<>
		{aviso}
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					type="button"
					size={size}
					variant={variant}
					className={className}
					disabled={discando || Boolean(ativa)}
					aria-label={dica}
					title={dica}
					onClick={(e) => e.stopPropagation()}
				>
					{conteudo}
					{mostrarRotulo ? "Ligar" : null}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuLabel>Ligar por</DropdownMenuLabel>
				{conexoes.map((w) => (
					<DropdownMenuItem
						key={w.id}
						onClick={(e) => {
							e.stopPropagation();
							pedirLigacao(w.id);
						}}
					>
						{w.name}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
		{botaoIa}
		</>
	);
};

export default BotaoDeVoz;
